import { ACTIVITY_LABEL_KEYS, computeGoalPlan, isSameGoal, unitWord, WORK_ACTIVITIES, WORK_UNITS } from "../../goals/goalRules";

/**
 * Toplu atama ve şablonlardaki hedef kalemleri (mobil goalItems.ts ile
 * aynı). Kalem: { label, goalValue, unit, activity, workId, workName,
 * totalValue, planDays, weekDays }; sunucudaki goalTemplate ve
 * bulkAssign.service aynı biçimi alır.
 */

/** Sunucudaki şablon adı sınırı (goalTemplate.constants TEMPLATE_NAME_MAX). */
export const TEMPLATE_NAME_MAX = 60;

/**
 * Hedef sihirbazının sonucu listeye girecek kaleme döner. Günlük hedef
 * bitirme planında toplam / gün.
 */
export function wizardToItem(value) {
  const goalValue =
    value.planDays && value.totalValue
      ? computeGoalPlan(value.totalValue, value.planDays).goalValue
      : (value.goalValue ?? 0);
  const item = {
    label: String(value.label || "").trim(),
    goalValue,
    unit: value.unit,
    activity: value.activity,
    workId: value.workId ?? null,
    totalValue: value.totalValue ?? null,
    planDays: value.planDays ?? null,
    weekDays: Array.isArray(value.weekDays) ? [...value.weekDays] : [],
  };
  if (value.workName) item.workName = value.workName;
  return item;
}

/** Listedeki kalemi düzenlemek için sihirbazın başlangıç değeri. */
export function itemToWizardInitial(item) {
  return {
    label: item.label,
    unit: WORK_UNITS.includes(item.unit) ? item.unit : "count",
    activity: WORK_ACTIVITIES.includes(item.activity) ? item.activity : "okuma",
    workId: item.workId ?? null,
    workName: item.workName,
    goalValue: item.goalValue,
    totalValue: item.totalValue ?? null,
    planDays: item.planDays ?? null,
    weekDays: Array.isArray(item.weekDays) ? [...item.weekDays] : [],
  };
}

/** Ekle ya da düzenle; aynı ad + biçim başka satırda varsa reddedilir. */
export function upsertGoal(goals, item, editIndex) {
  const clash = goals.some((g, i) => i !== editIndex && isSameGoal(g, item.label, item.activity));
  if (clash) return { ok: false };
  const next = editIndex === null || editIndex === undefined
    ? [...goals, item]
    : goals.map((g, i) => (i === editIndex ? item : g));
  return { ok: true, goals: next };
}

export const removeGoal = (goals, index) => goals.filter((_, i) => i !== index);

/** Şablona basınca liste bu kopyayla dolar; şablonun kendisi değişmez. */
export function copyTemplateGoals(template) {
  return (template?.goals || []).map((g) => ({ ...g, weekDays: [...(g.weekDays ?? [])] }));
}

/** Biçimin kısa adı: "Okuma", "Ezber". */
export function activityText(activity, t) {
  return t(ACTIVITY_LABEL_KEYS[activity] || ACTIVITY_LABEL_KEYS.okuma);
}

/** Hedef satırının alt yazısı: "Ezber · Günlük hedef 10 sayfa" ya da "Okuma · 600 sayfa / 30 gün". */
export function goalItemMeta(item, t) {
  const word = unitWord(item.unit, t);
  const amount =
    item.planDays && item.totalValue
      ? `${item.totalValue} ${word} / ${item.planDays} ${t("gw_days")}`
      : `${t("gw_daily_goal")} ${item.goalValue ?? 0} ${word}`;
  return `${activityText(item.activity, t)} · ${amount}`;
}

/** Şablon satırının alt yazısı: "3 hedef · 12 kez kullanıldı". */
export function templateMeta(template, t) {
  const used =
    template.useCount > 0 ? t("bulk_template_used", { n: template.useCount }) : t("bulk_template_never_used");
  return `${t("bulk_goal_count", { n: (template.goals || []).length })} · ${used}`;
}

/**
 * Liste şablondan geldiyse güncelle ya da yeni olarak kaydet; değilse ya da
 * şablon bu arada silinmişse yalnız yeni kayıt.
 */
export function saveOptions(templateId, templates) {
  return templateId && (templates || []).some((tpl) => tpl._id === templateId)
    ? ["update", "saveAsNew"]
    : ["saveNew"];
}

/** Sunucuya giden kalem: yalnız dolu alanlar, eser adı yalnız gösterim için kalır. */
export function toServerItem(item) {
  const out = { label: String(item.label || "").trim(), goalValue: Number(item.goalValue) || 0 };
  if (item.unit) out.unit = item.unit;
  if (item.activity) out.activity = item.activity;
  if (item.workId) out.workId = String(item.workId);
  if (item.workName) out.workName = item.workName;
  if (item.totalValue != null) out.totalValue = item.totalValue;
  if (item.planDays != null) out.planDays = item.planDays;
  out.weekDays = Array.isArray(item.weekDays) ? [...item.weekDays] : [];
  return out;
}

/** Şablon oluşturma gövdesi: POST /goal-templates/create. */
export function createTemplateBody(name, goals) {
  return { name: String(name || "").trim(), goals: goals.map(toServerItem) };
}

/** Şablon güncelleme gövdesi: gelmeyen alan değişmez (PUT /goal-templates/update). */
export function updateTemplateBody(templateId, { name, goals } = {}) {
  const body = { templateId };
  if (name !== undefined) body.name = String(name).trim();
  if (goals !== undefined) body.goals = goals.map(toServerItem);
  return body;
}

/** Toplu atama gövdesi: POST /groups/bulk-assign-goals. */
export function bulkAssignBody({ groupId, memberIds, goals, templateId, dryRun, localDate }) {
  const body = { groupId, memberIds: [...memberIds], goals: goals.map(toServerItem), localDate };
  if (templateId) body.templateId = templateId;
  if (dryRun) body.dryRun = true;
  return body;
}
